// File: frontend/src/layouts/Footer.tsx
import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { useCookieConsent } from "../context/CookieConsentContext";
import { LEGAL_ROUTES } from "../services/legalService";
import { useIsPwaStandalone } from "../hooks/useIsPwaStandalone";
import "./Footer.css";

export const Footer: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { openPreferences } = useCookieConsent();
  // In the installed app the footer stacks into four rows on a phone and eats
  // most of the visible content area. Standalone mode therefore gets a single
  // compact row: the legal links stay (they must be reachable from every
  // page), the copyright and the clock are dropped — the OS already shows the
  // time in the status bar.
  const isStandalone = useIsPwaStandalone();

  // Live clock — ticks every second so the footer date/time stays current
  // without a page refresh (kahvehane duvar saati).
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const intervalId = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(intervalId);
  }, []);

  const locale = i18n.language === "tr" ? "tr-TR" : "en-US";
  const dateStr = now.toLocaleDateString(locale, {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });
  const timeStr = now.toLocaleTimeString(locale, {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });

  return (
    <div className={`footer${isStandalone ? " footer--compact" : ""}`}>
      {!isStandalone && (
        <span className="footer-copyright">
          © {now.getFullYear()} 3 Korner 1 Penaltı
        </span>
      )}

      {/* Legal links — the documents have to be reachable from every page,
          which is the whole point of putting them in the footer. Cookie
          preferences opens the consent dialog rather than navigating, so the
          user does not lose their place. */}
      <nav className="footer-links">
        <Link to="/iletisim" className="footer-link">
          {t("footer_contact", "İletişim")}
        </Link>
        <Link to={LEGAL_ROUTES.RULES} className="footer-link">
          {t("legal_link_rules", "Hamur Abi Kuralları")}
        </Link>
        <Link to={LEGAL_ROUTES.TERMS} className="footer-link">
          {t("legal_link_terms", "Kullanım Koşulları")}
        </Link>
        <Link to={LEGAL_ROUTES.PRIVACY} className="footer-link">
          {t("legal_link_privacy", "Gizlilik Politikası")}
        </Link>
        <Link to={LEGAL_ROUTES.COOKIE} className="footer-link">
          {t("legal_link_cookie", "Çerez Politikası")}
        </Link>
        <button type="button" className="footer-link footer-link-btn" onClick={openPreferences}>
          {t("legal_link_cookie_prefs", "Çerez Tercihleri")}
        </button>
      </nav>

      {!isStandalone && (
        <span className="footer-clock">
          {dateStr} · {timeStr}
        </span>
      )}
    </div>
  );
};